import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  DEFAULT_MODEL_IMAGE_FRAME,
  computeModelImageLayout,
  modelImageFrameStyle,
  normalizeModelImageFrame,
  type AutomobileModelImageFrame,
} from '../../utils/automobileModelImageFrame';

const TRANSPARENCY_GRID =
  'repeating-conic-gradient(#d1d5db 0% 25%, transparent 0% 50%) 50% / 16px 16px';

type Props = {
  src: string;
  alt: string;
  frame?: Partial<AutomobileModelImageFrame> | null;
  className?: string;
  imgClassName?: string;
  onError?: () => void;
};

const AutomobileModelImage: React.FC<Props> = ({
  src,
  alt,
  frame,
  className = '',
  imgClassName = '',
  onError,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [failed, setFailed] = useState(false);
  const [box, setBox] = useState({ w: 0, h: 0 });
  const [natural, setNatural] = useState({ w: 0, h: 0 });
  const resolvedFrame = normalizeModelImageFrame(frame ?? DEFAULT_MODEL_IMAGE_FRAME);

  useEffect(() => {
    setFailed(false);
    setNatural({ w: 0, h: 0 });
  }, [src]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      setBox({ w: width, h: height });
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const layout = useMemo(() => {
    if (box.w <= 0 || box.h <= 0 || natural.w <= 0 || natural.h <= 0) return null;
    return computeModelImageLayout(resolvedFrame, box.w, box.h, natural.w, natural.h);
  }, [resolvedFrame, box.w, box.h, natural.w, natural.h]);

  const fallbackStyle = modelImageFrameStyle(resolvedFrame);
  const useComputedLayout = layout != null;

  if (!src || failed) return null;

  return (
    <div
      ref={containerRef}
      className={`absolute inset-0 overflow-hidden ${className}`}
      style={layout?.showTransparentLetterbox ? { backgroundImage: TRANSPARENCY_GRID } : undefined}
    >
      <img
        key={src}
        src={src}
        alt={alt}
        loading="lazy"
        decoding="async"
        draggable={false}
        className={
          useComputedLayout
            ? imgClassName
            : `w-full h-full object-cover ${imgClassName}`
        }
        style={useComputedLayout ? layout.imgStyle : fallbackStyle}
        onLoad={(event) => {
          setNatural({
            w: event.currentTarget.naturalWidth,
            h: event.currentTarget.naturalHeight,
          });
        }}
        onError={() => {
          setFailed(true);
          onError?.();
        }}
      />
    </div>
  );
};

export default AutomobileModelImage;
